import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import useWindowDimensions from "../Account/WindowDimensions";
import { useAuthToken } from "../AuthProvider";
import { toast } from "react-toastify";
import { User, UserScore } from "../Profile";
import SingleTemplateDisplay from "./SingleTemplateDisplay";
import arrow_forward from "../img/arrow_forward.svg";
import arrow_back from "../img/arrow_back.svg";
import "react-tooltip/dist/react-tooltip.css";

interface TemplateParams {
  name: string;
  contents: any;
}

function GetWidthDivisor(width: number) {
  if (width >= 1280) {
    return 6;
  }

  if (width >= 1024) {
    return 3.1;
  }

  if (width >= 768) {
    return 3;
  }

  return 1.5;
}

const RESUMES_PER_PAGE = 10;

interface TemplatesDisplayParams {
  callback: () => void;
}

export function TemplatesDisplay(params: TemplatesDisplayParams) {
  const dispatch = useDispatch();
  const userData = useSelector((state: AppState) => state.user); // get the current user object from the Redux store
  const [templateParams, setTemplateParams] = React.useState<TemplateParams[]>(
    []
  );
  const [divisor, setDivisor] = React.useState<number>(1);
  const [shownPage, setShownPage] = React.useState<number>(1);
  const [shownTemplateDetails, setShownTemplateDetails] = React.useState<
    TemplateParams[]
  >([]);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [numTemplates, setNumTemplates] = React.useState(0);
  const { isAuthenticated, token, isLoading, updateToken } = useAuthToken();
  const navigate = useNavigate();

  const { height, width } = useWindowDimensions();

  React.useEffect(() => {
    if (!isLoading) {
      setDivisor(GetWidthDivisor(width));
    }
  }, [width, isAuthenticated, isLoading]);

  const updatePreferredTemplate = (preferred_template: string) => {
    const updatedUser: User = {
      ...userData, // create a new object with all existing properties of the user object
      preferred_template: preferred_template,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  async function getTemplates() {
    if (isLoading) {
      return;
    }

    const apiEndpoint =  "/api/templates";

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      // Make a POST request using fetch
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      });

      const responseData = await response.json();

      setTemplateParams(responseData.template_details);
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  useEffect(() => {
    if (!isLoading) {
      getTemplates();
    }
  }, [isLoading]);

  React.useEffect(() => {
    setNumTemplates(templateParams.length);
    setTotalPages(Math.ceil(templateParams.length / RESUMES_PER_PAGE));

    if (templateParams.length == 0) {
      setTotalPages(1);
    }
  }, [templateParams]);

  React.useEffect(() => {
    if (templateParams.length <= RESUMES_PER_PAGE) {
      setShownTemplateDetails(templateParams);
    } else {
      if (shownPage * RESUMES_PER_PAGE >= templateParams.length) {
        setShownTemplateDetails(
          templateParams.slice((shownPage - 1) * RESUMES_PER_PAGE)
        );
      } else {
        setShownTemplateDetails(
          templateParams.slice(
            (shownPage - 1) * RESUMES_PER_PAGE,
            shownPage * RESUMES_PER_PAGE
          )
        );
      }
    }
  }, [shownPage, totalPages, templateParams]);

  return (
    <div>
      <div className="flex max-w-full flex-row">
        <div className="flex w-full flex-col">
          <div>
            <div className="flex w-full flex-row justify-between">
              <div className="flex w-full flex-col justify-between">
                <div className="ml-auto mr-8 mt-4 flex flex-row">
                  <button
                    className="mr-2"
                    onClick={() => {
                      if (shownPage > 1) {
                        setShownPage(shownPage - 1);
                      }
                    }}
                  >
                    <img src={arrow_back} />
                  </button>
                  <h1 className="font-code font-bold text-black">
                    Page {shownPage} of {totalPages}
                  </h1>
                  <button
                    className="ml-2"
                    onClick={() => {
                      if (shownPage < totalPages) {
                        setShownPage(shownPage + 1);
                      }
                    }}
                  >
                    <img src={arrow_forward} />
                  </button>
                </div>
              </div>
            </div>
            <div>
              <h1 className="px-8 font-code text-xl lg:px-16">
                Pick a template for your autogenerated resume!
              </h1>
              <div className="px-8 py-8 lg:px-16 lg:py-16">
                <div className="grid max-h-[50vh] grid-cols-1 gap-x-4 gap-y-8 overflow-y-scroll md:grid-cols-2 lg:max-h-[70vh] lg:gap-x-2 xl:grid-cols-4">
                  {shownTemplateDetails.map((details, index) => (
                    <div key={index}>
                      <SingleTemplateDisplay
                        width={width / divisor}
                        data={details.contents}
                        filename={details.name}
                        callback={(preferred_template: string) => {
                          updatePreferredTemplate(preferred_template);
                          params.callback();
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
